import type { HTMLAttributes, ReactNode } from 'react';

const PADDING_CLASS = {
  /** 표·리스트를 가장자리까지 붙여 넣을 때(카드가 컨테이너 역할만). */
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-5',
  /** 작성·상세 폼 섹션(목업 `.tbm-section`·`.ir-section` 22~26px). */
  xl: 'p-6',
} as const;

/**
 * 라운드는 prop으로만 바꾼다 — `className`에 rounded-*를 덧붙이면 Tailwind 생성
 * CSS 순서에 따라 어느 쪽이 이길지 보장되지 않아(클래스 나열 순서 무관) 조용히 무시될 수 있다.
 */
const RADIUS_CLASS = {
  /**
   * 조밀 박스·썸네일(8px). 12px을 주면 부풀어 보이는 자리 —
   * 반지름 판정표가 `p-2` 이하 박스와 이미지 모서리를 여기로 보낸다.
   */
  sm: 'rounded-card-sm',
  /** 중간 박스(12px). */
  md: 'rounded-card',
  /** 페이지 최상위 카드·패널(18px, 목업 --radius-lg). */
  lg: 'rounded-card-lg',
} as const;

/**
 * 그림자도 라운드와 **같은 이유로** prop이다. 예전 주석은 "`className`으로 `shadow-card`를
 * 덮어쓴다"고 안내했지만 실제로는 동작하지 않는다 — 생성된 CSS에서 `.shadow-card`가
 * `.shadow-card-sm`보다 **앞에** 나오므로(preset의 boxShadow 키 순서) 둘 다 붙으면
 * 항상 기본값인 `shadow-card-sm`이 이긴다. 클래스를 통째로 갈아끼워야 의도대로 된다.
 *
 * 목업의 3단: 목록 페이지 패널 `--shadow-sm` / 폼 섹션 `--shadow` / 안쪽 박스는 그림자 없음.
 */
const ELEVATION_CLASS = {
  /** 안쪽 박스 — 테두리만(목업 `.sub-card`·`.evc`·`.fp-card`). */
  none: '',
  /** 정적인 면(목업 `.stat`·`.toolbar`·`.table-card`). */
  sm: 'shadow-card-sm',
  /** 떠오르는 강조(목업 `.tbm-section`·`.ir-section`·`.stat:hover`). */
  md: 'shadow-card',
} as const;

/**
 * 면과 테두리는 **짝으로** 움직인다 — 목업에서 옅은 면(`--soft`)을 쓰는 카드는 테두리도
 * 한 단계 옅은 `--line-soft`를 쓴다(`.sub-card`·`.res-status`·`.appr-flow`). 호출부가
 * `className="bg-surface-soft"`만 덧붙이면 테두리가 진한 채로 남아 조용히 어긋나므로
 * 한 prop이 둘 다 소유한다.
 */
const SURFACE_CLASS = {
  white: 'border-line bg-white',
  /** 카드 안에 다시 놓이는 보조 면. */
  soft: 'border-line-soft bg-surface-soft',
} as const;

export type CardPadding = keyof typeof PADDING_CLASS;
export type CardRadius = keyof typeof RADIUS_CLASS;
export type CardElevation = keyof typeof ELEVATION_CLASS;
export type CardSurface = keyof typeof SURFACE_CLASS;

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** 내부 여백. 기본 md. 표를 담을 땐 none + 자식이 여백 소유. */
  padding?: CardPadding;
  /** 모서리 라운드. 기본 md(12px), 페이지 최상위 카드는 lg(18px), 조밀 박스·썸네일은 sm(8px). */
  radius?: CardRadius;
  /** 그림자 단계. 기본 sm. 카드 안 보조 박스는 none. */
  elevation?: CardElevation;
  /** 면 색. 기본 white. 카드 안에 겹쳐 놓는 보조 면은 soft(테두리도 함께 옅어진다). */
  surface?: CardSurface;
  children?: ReactNode;
  /** 추가 클래스. */
  className?: string;
}

/**
 * 공용 카드 — 흰 면 + 토큰 테두리/라운드/그림자의 단일 소스.
 *
 * ## className으로 덮어쓸 수 있는 것과 없는 것
 * `overflow-hidden`·`flex`·`w-full`처럼 **기본 클래스와 겹치지 않는** 유틸은 `className`으로
 * 얼마든지 붙여도 된다. 반대로 여백·라운드·그림자·면처럼 **같은 CSS 속성을 다투는** 것은
 * 반드시 prop으로 정한다 — 겹쳐 붙이면 Tailwind가 생성한 CSS 순서가 승자를 정하므로
 * 클래스를 나열한 순서와 무관하게 조용히 무시될 수 있다.
 * 표에 없는 값이 필요하면(예: `p-8`) 해당 prop을 끄고(`padding="none"`) className으로 준다.
 */
export function Card({
  padding = 'md',
  radius = 'md',
  elevation = 'sm',
  surface = 'white',
  className = '',
  children,
  ...rest
}: CardProps) {
  return (
    <div
      className={`${RADIUS_CLASS[radius]} border ${SURFACE_CLASS[surface]} ${ELEVATION_CLASS[elevation]} ${PADDING_CLASS[padding]} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}
